import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Search, CheckCircle2, Save } from 'lucide-react';
import { money, formatWeight } from '@vj/shared';
import { api, ApiClientError } from '../../lib/api.js';
import { enqueueDraft } from '../../lib/offlineQueue.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { Button } from '../../components/ui/Button.jsx';
import { TextField, SelectField, TextAreaField } from '../../components/ui/Field.jsx';
import { WeightInput } from '../../components/ui/WeightInput.jsx';
import { Modal } from '../../components/ui/Modal.jsx';

const mobilePattern = /^[6-9]\d{9}$/;

function newItemRow() {
  return { key: crypto.randomUUID(), itemName: '', weight: '0', repairDetails: '', remarks: '' };
}

export default function RepairForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [customer, setCustomer] = useState({ id: null, name: '', mobile: '' });
  const [karigarId, setKarigarId] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [estimatedCharge, setEstimatedCharge] = useState('0');
  const [advanceAmount, setAdvanceAmount] = useState('0');
  const [items, setItems] = useState([newItemRow()]);
  const [notes, setNotes] = useState('');
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [pendingAction, setPendingAction] = useState(null);

  const [karigarModalOpen, setKarigarModalOpen] = useState(false);
  const [newKarigar, setNewKarigar] = useState({ name: '', mobile: '' });

  const karigarsQuery = useQuery({ queryKey: ['masters', 'suppliers', 'karigar'], queryFn: () => api.get('/masters/suppliers', { query: { type: 'karigar' } }) });

  const addKarigarMutation = useMutation({
    mutationFn: () => api.post('/masters/suppliers', { name: newKarigar.name, supplierType: 'karigar', mobile: newKarigar.mobile || undefined }),
    onSuccess: (supplier) => {
      queryClient.invalidateQueries({ queryKey: ['masters', 'suppliers', 'karigar'] });
      setKarigarId(String(supplier.id));
      setKarigarModalOpen(false);
      setNewKarigar({ name: '', mobile: '' });
    },
  });

  // --- Customer lookup by mobile -------------------------------------------
  const debouncedMobile = useDebouncedValue(customer.mobile, 400);
  const canLookup = mobilePattern.test(debouncedMobile) && !customer.id;
  const lookupQuery = useQuery({
    queryKey: ['customers', 'by-mobile', debouncedMobile],
    queryFn: () => api.get(`/customers/by-mobile/${debouncedMobile}`),
    enabled: canLookup,
  });
  const matches = canLookup ? lookupQuery.data ?? [] : [];

  const selectCustomer = (c) => setCustomer({ id: c.id, name: c.name, mobile: c.mobile ?? '' });
  const clearCustomerMatch = () => setCustomer((c) => ({ ...c, id: null }));

  const updateItem = (key, patch) => setItems((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeItem = (key) => setItems((rows) => (rows.length > 1 ? rows.filter((r) => r.key !== key) : rows));
  const addItem = () => setItems((rows) => [...rows, newItemRow()]);

  const totalWeight = items.reduce((acc, i) => acc.plus(i.weight || 0), money(0));

  const createMutation = useMutation({
    mutationFn: (payload) => api.post('/repairs', payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['repairs'] });
      navigate(`/repairs/${result.id}`, { state: { justCreated: true } });
    },
    onError: (err, payload) => {
      // Only a draft-save queues offline — issuing needs a server-allocated
      // number at the moment it happens (docs/project-plan.html §13).
      if (err instanceof ApiClientError && err.isOffline && pendingAction === 'draft') {
        enqueueDraft({ endpoint: '/repairs', payload, docType: 'repair', label: customer.name || 'Untitled repair voucher' })
          .then(() => navigate('/repairs', { state: { savedOffline: true } }))
          .catch(() => setSubmitError('Could not save this draft on this device either. Please try again.'));
        return;
      }
      if (err instanceof ApiClientError && err.details) setFieldErrors(err.details);
      setSubmitError(err instanceof ApiClientError ? err.message : 'Could not save this voucher. Please try again.');
    },
  });

  const buildPayload = (saveAsDraft = false) => ({
    saveAsDraft,
    customerId: customer.id ?? undefined,
    customerName: customer.name,
    customerMobile: customer.mobile || undefined,
    karigarId: karigarId ? Number(karigarId) : undefined,
    expectedDeliveryDate: expectedDeliveryDate || undefined,
    estimatedCharge: Number(estimatedCharge),
    advanceAmount: Number(advanceAmount),
    items: items.map((item) => ({
      itemName: item.itemName,
      weight: Number(item.weight),
      repairDetails: item.repairDetails,
      remarks: item.remarks || undefined,
    })),
    notes: notes || undefined,
  });

  const canSubmit = !!customer.name.trim() && items.every((i) => i.itemName.trim() && i.repairDetails.trim());

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);
    setFieldErrors({});
    setPendingAction('issue');
    createMutation.mutate(buildPayload(false));
  };

  const onSaveDraft = () => {
    setSubmitError(null);
    setFieldErrors({});
    setPendingAction('draft');
    createMutation.mutate(buildPayload(true));
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5 pb-28">
      <h1 className="font-display text-2xl font-semibold tracking-tight">New repair voucher</h1>

      <section className="card flex flex-col gap-4 p-5">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Customer</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="relative">
            <TextField
              label="Mobile"
              inputMode="numeric"
              value={customer.mobile}
              onChange={(e) => setCustomer((c) => ({ ...c, mobile: e.target.value, id: c.id && e.target.value !== c.mobile ? null : c.id }))}
              hint={customer.id ? 'Matched to a saved customer' : 'Type 10 digits to search'}
            />
            {matches.length > 0 ? (
              <div className="absolute z-10 mt-1 w-full rounded-card border border-border bg-surface shadow-lg">
                {matches.map((m) => (
                  <button key={m.id} type="button" onClick={() => selectCustomer(m)} className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm hover:bg-surface-2">
                    <Search size={14} className="text-muted" aria-hidden="true" />
                    <span className="font-medium">{m.name}</span>
                    <span className="text-muted">{m.city}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <TextField label="Name" required value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} />
        </div>
        {customer.id ? (
          <button type="button" onClick={clearCustomerMatch} className="self-start text-sm text-primary hover:underline">
            Not this customer — enter details manually
          </button>
        ) : null}
      </section>

      <section className="card grid grid-cols-1 gap-3 p-5 sm:grid-cols-2">
        <div>
          <SelectField label="Aasari (optional)" value={karigarId} onChange={(e) => setKarigarId(e.target.value)}>
            <option value="">Not assigned yet</option>
            {(karigarsQuery.data ?? []).map((k) => (
              <option key={k.id} value={k.id}>{k.name}</option>
            ))}
          </SelectField>
          <button type="button" onClick={() => setKarigarModalOpen(true)} className="mt-1.5 text-sm text-primary hover:underline">
            + Add a new aasari
          </button>
        </div>
        <TextField label="Expected delivery date" type="date" value={expectedDeliveryDate} onChange={(e) => setExpectedDeliveryDate(e.target.value)} />
        <TextField label="Estimated charge (₹)" type="number" inputMode="decimal" value={estimatedCharge} onChange={(e) => setEstimatedCharge(e.target.value)} />
        <TextField label="Advance paid (₹)" type="number" inputMode="decimal" value={advanceAmount} onChange={(e) => setAdvanceAmount(e.target.value)} />
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Items received</h2>
          <Button type="button" variant="secondary" size="sm" onClick={addItem}>
            <Plus size={15} aria-hidden="true" /> Add item
          </Button>
        </div>
        {items.map((item) => (
          <div key={item.key} className="card flex flex-col gap-3 p-4">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <TextField label="Item name" required value={item.itemName} onChange={(e) => updateItem(item.key, { itemName: e.target.value })} />
              </div>
              {items.length > 1 ? (
                <button type="button" onClick={() => removeItem(item.key)} className="mt-7 flex h-touch w-touch shrink-0 items-center justify-center rounded-card text-muted hover:bg-danger/10 hover:text-danger" aria-label="Remove item">
                  <Trash2 size={18} aria-hidden="true" />
                </button>
              ) : null}
            </div>
            <WeightInput label="Weight" value={item.weight} onChange={(v) => updateItem(item.key, { weight: v })} />
            <TextAreaField label="Repair required" required rows={2} value={item.repairDetails} onChange={(e) => updateItem(item.key, { repairDetails: e.target.value })} />
            <TextField label="Remarks" value={item.remarks} onChange={(e) => updateItem(item.key, { remarks: e.target.value })} />
          </div>
        ))}
      </section>

      <TextAreaField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />

      <section className="card ml-auto w-full max-w-sm p-5">
        <div className="flex justify-between text-sm">
          <span className="text-muted">Total weight</span>
          <span className="tabular font-semibold">{formatWeight(totalWeight)}</span>
        </div>
      </section>

      {submitError ? (
        <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {submitError}
        </p>
      ) : null}

      <div className="raised fixed inset-x-0 bottom-0 z-20 flex items-center justify-end gap-2 border-t border-border bg-surface px-4 py-3 shadow-raised sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
        <Button
          type="button"
          variant="secondary"
          size="lg"
          disabled={!canSubmit}
          loading={createMutation.isPending && pendingAction === 'draft'}
          onClick={onSaveDraft}
        >
          <Save size={18} aria-hidden="true" /> Save as draft
        </Button>
        <Button type="submit" size="lg" disabled={!canSubmit} loading={createMutation.isPending && pendingAction === 'issue'}>
          <CheckCircle2 size={18} aria-hidden="true" /> Save voucher
        </Button>
      </div>

      <Modal open={karigarModalOpen} onClose={() => setKarigarModalOpen(false)} title="Add an Aasari">
        <div className="flex flex-col gap-4">
          <TextField label="Name" required value={newKarigar.name} onChange={(e) => setNewKarigar((k) => ({ ...k, name: e.target.value }))} />
          <TextField label="Mobile" inputMode="numeric" value={newKarigar.mobile} onChange={(e) => setNewKarigar((k) => ({ ...k, mobile: e.target.value }))} />
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setKarigarModalOpen(false)}>Cancel</Button>
            <Button type="button" disabled={!newKarigar.name.trim()} loading={addKarigarMutation.isPending} onClick={() => addKarigarMutation.mutate()}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </form>
  );
}
